import { Component, computed, inject, input, output } from '@angular/core'
import { getUsageHeaderSuffix, IdrColDef, IdrColumnMetadata, IdrColumnSpec } from './idr-columns'
import { FilterParams, SortParams } from '../shared/utilities/http-params'
import { ColumnSelection, IdrColumnsSelectorComponent } from './idr-columns-selector.component'
import { IdrFiltersTableComponent } from './idr-filters-table.component'
import { IdrUserSettingsService } from './idr-user-settings.service'


const getHourlyUsageSelection = (columnDefs: IdrColDef[], metadata: IdrColumnMetadata): ColumnSelection => {
  const hourlyColDefs = columnDefs.filter(_ => metadata.columnSpecsByField[_.field].hourlyUsage)
  return {
    id: 'h01-h24',
    label: 'H01-24' + getUsageHeaderSuffix('kwh'),
    selected: !hourlyColDefs[0]?.hide,
    columnType: 'optional',
    colDefs: hourlyColDefs,
  }
}

const getUnarySelection = (columnDef: IdrColDef, columnSpec: IdrColumnSpec): ColumnSelection => ({
  id: columnSpec.field,
  selected: !columnDef.hide,
  label: columnDef.headerName!,
  columnType: columnSpec.columnType,
  colDefs: [columnDef],
})

const getSelections = (columnDefs: IdrColDef[], metadata: IdrColumnMetadata): ColumnSelection[] => {
  // make sure we retain the same order in selections as the column defs (other than keeping the hourlies together)
  const selections: ColumnSelection[] = []
  let hourlyUsageFound = false
  columnDefs.forEach(columnDef => {
    const columnSpec = metadata.columnSpecsByField[columnDef.field]
    if (!columnSpec.hourlyUsage) {
      selections.push(getUnarySelection(columnDef, columnSpec))
    } else if (!hourlyUsageFound) {
      selections.push(getHourlyUsageSelection(columnDefs, metadata))
      hourlyUsageFound = true
    }
  })

  return selections
}

@Component({
  selector: 'app-idr-configuration',
  imports: [
    IdrColumnsSelectorComponent,
    IdrFiltersTableComponent,
  ],
  template: `
      <div class='filters'>
          <app-idr-filters-table
            [filters]="filters()"
            [columnMetadata]="columnMetadata()"
            [editedFilter]="editedFilter()"
            [ready]="columnDefs().length > 0"
            (filtersChange)="onFiltersChange($event)"
            (editedFilterReset)="editedFilterReset.emit()"
          />
      </div>
      <div class='columns'>
          <app-idr-columns-selector
            [selections]="selections()"
            (selectionsChange)="handleSelectionsChange($event)"
          />
      </div>
  `,
  styles: `
    :host {
      display: flex;
      column-gap: 5px;
    }
    .filters, .columns {
      flex-basis: 0;
    }
    .columns {
      flex-grow: 1;
    }
    .filters {
      flex-grow: 2;
    }
  `,
})
export class IdrConfigurationComponent {
  private readonly userSettingsService = inject(IdrUserSettingsService)

  readonly columnDefs = input.required<IdrColDef[]>()
  readonly columnMetadata = input.required<IdrColumnMetadata>()
  readonly filters = input.required<readonly FilterParams[]>()
  readonly orderBy = input.required<readonly SortParams[]>()
  readonly editedFilter = input<string>()
  readonly columnsChange = output<IdrColDef[]>()
  readonly filtersChange = output<readonly FilterParams[]>()
  readonly orderByChange = output<readonly SortParams[]>()
  readonly editedFilterReset = output()

  protected readonly selections = computed<ColumnSelection[]>(() =>
    getSelections(this.columnDefs(), this.columnMetadata()),
  )

  protected handleSelectionsChange(selections: readonly ColumnSelection[]) {
    this.columnsChange.emit(selections.flatMap(({ colDefs, selected }) =>
      colDefs.map(d => d.hide === selected ? { ...d, hide: !d.hide } : d),
    ))
  }

  protected onFiltersChange(filters: readonly FilterParams[]) {
    if (filters.some(_ => _.name === 'operating_date')) {
      this.userSettingsService.saveFilters(filters)
    }
    this.filtersChange.emit(filters)
  }
}
